import { Injectable, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable } from 'rxjs';

import { getCookieValue, removeCookie, setCookieValue } from '@acme/b2b/utils';
import type { Impersonation, UserSession } from '@b2b/common/services/auth/types';

import { ApiService } from '../api/api.service';
import { FeatureToggleService } from '../feature-toggle/feature-toggle.service';
import { PermissionsService } from '../permissions/permissions.service';
import { TenantService } from '../tenant/tenant.service';
import { CsrfInterceptor } from './csrf.interceptor';

// ============= State machine types =============

export type AuthenticationStatus = 'unknown' | 'authenticated' | 'unauthenticated';

export interface AuthenticationState {
  state: AuthenticationStatus;
  session: UserSession | null;
  impersonation: Impersonation | null;
}

export type AuthenticationEvents =
  | { type: 'session.initialized'; params: { session: UserSession | null; impersonation: Impersonation | null } }
  | { type: 'login.success'; params: { session: UserSession } }
  | { type: 'logout' }
  | { type: 'csr.impersonation.changed'; params: { session: UserSession; impersonation: Impersonation | null } }
  | { type: 'sso.callback.success'; params: { session: UserSession } };

function createInitialState(values: Partial<AuthenticationState> = {}): AuthenticationState {
  return {
    state: 'unknown',
    session: null,
    impersonation: null,
    ...values,
  };
}

// ============= Response shapes =============
//
// The HTTP layer (UserApiService / SystemApiService / legacy bridges) returns
// `{ data: <body> }`. The body shapes below describe what each backend payload
// looks like — used to type the casts when reading delegated responses.

interface CsrfBody {
  success?: boolean;
  payload: { token: string };
}

interface PermissionsBody {
  success?: boolean;
  payload: string[];
}

interface TenantConfigBody {
  success?: boolean;
  payload: { list: Array<Record<string, unknown>> };
}

interface DeputySignInBody {
  success: boolean;
  payload: {
    user: UserSession;
    impersonation: Impersonation | null;
  };
}

interface LoginBody {
  session: UserSession;
}

interface AjsResponse<T> {
  data: T;
}

// ============= Service =============

/**
 * Unified Authentication Service (Angular 18).
 *
 * Replaces both AngularJS legacy services:
 *  - apps/b2b-ui/common/services/auth/auth.service.js
 *  - apps/b2b-ui/common/services/auth/authentication.service.ts
 *
 * Exposed to AngularJS via downgradeInjectable under the names
 * 'AuthenticationService' and 'AuthService' (see ng-downgraded-components.ts).
 *
 * Domain layer — wraps the HTTP layer (`ApiService` facade) with cookie/CSRF
 * management, reactive state, impersonation logic and session bootstrap.
 * All HTTP calls delegate to `this.api.*` so that endpoints live next to their
 * sub-service, not inside auth.
 */
@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private stateSubject = new BehaviorSubject<AuthenticationState>(createInitialState());
  readonly state$: Observable<AuthenticationState> = this.stateSubject.asObservable();

  // ============= Reactive signal surface =============
  // Signal mirror of the auth state machine, for Angular consumers (e.g. the
  // migrated app-header) that derive UI from auth state instead of listening to
  // the legacy `AppController::login/logout::success` $scope events.

  /** Signal mirror of `state$` (BehaviorSubject seeds the synchronous value). */
  private readonly authState = toSignal(this.state$, { requireSync: true });

  /** The active session user, or `null`. */
  readonly user: Signal<UserSession | null> = computed(() => this.authState().session);

  /** The active CSR impersonation, or `null`. */
  readonly csr: Signal<Impersonation | null> = computed(() => this.authState().impersonation);

  /** Whether a user session is active. */
  readonly isLoggedIn: Signal<boolean> = computed(() => this.authState().state === 'authenticated');

  // Legacy fields preserved for callers that read them directly.
  roles: string[] = [];
  CSRF: { token?: string; [key: string]: unknown } | null = null;

  // Use inject() (Angular 14+) instead of constructor injection to avoid
  // emitDecoratorMetadata edge cases in the hybrid AngularJS + Angular bundle.
  private api = inject(ApiService);
  private csrfInterceptor = inject(CsrfInterceptor);
  private permissions = inject(PermissionsService);
  private tenant = inject(TenantService);
  private featureToggle = inject(FeatureToggleService);

  constructor() {
    this.initializeSession();
  }

  // ============= State =============

  getSnapshot(): AuthenticationState {
    return this.stateSubject.value;
  }

  send(event: AuthenticationEvents): void {
    switch (event.type) {
      case 'session.initialized':
        this.stateSubject.next({
          ...this.stateSubject.value,
          state: event.params.session ? 'authenticated' : 'unauthenticated',
          session: event.params.session,
          impersonation: event.params.impersonation,
        });
        break;
      case 'login.success':
        this.stateSubject.next({
          ...this.stateSubject.value,
          state: 'authenticated',
          session: event.params.session,
        });
        break;
      case 'logout':
        this.stateSubject.next({ state: 'unauthenticated', session: null, impersonation: null });
        break;
      case 'csr.impersonation.changed':
        this.stateSubject.next({
          ...this.stateSubject.value,
          session: event.params.session,
          impersonation: event.params.impersonation,
        });
        break;
      case 'sso.callback.success':
        this.stateSubject.next({
          ...this.stateSubject.value,
          state: 'authenticated',
          session: event.params.session,
        });
        break;
    }
  }

  // ============= Login / Logout =============

  login(props: {
    email: string;
    password: string;
    header?: { name: string; value: string };
  }): Promise<AjsResponse<LoginBody>> {
    return this.api.user.login(props) as Promise<AjsResponse<LoginBody>>;
  }

  logout(): Promise<AjsResponse<{ success: boolean }>> {
    return (this.api.user.logout() as Promise<AjsResponse<{ success: boolean }>>).then((response) => {
      this.destroy();
      return response;
    });
  }

  confirm(props: { token: string }): Promise<AjsResponse<unknown>> {
    return this.api.user.confirm(props);
  }

  preFlightCheck(hostname: string): Promise<AjsResponse<{ success: boolean }>> {
    return this.api.system.preFlightCheck({ hostName: hostname }) as Promise<
      AjsResponse<{ success: boolean }>
    >;
  }

  ssoCallback(params: { RelayState: string }): Promise<AjsResponse<LoginBody>> {
    return this.api.user.ssoCallback(params) as Promise<AjsResponse<LoginBody>>;
  }

  deputySignIn(userId: number): Promise<AjsResponse<DeputySignInBody>> {
    // Bridged to the legacy CustomerServiceApiService until that sub-service
    // is migrated. The legacy method is `deputySignIn(tenantId)`.
    const customerService = this.api.customerService as {
      deputySignIn(id: number): Promise<AjsResponse<DeputySignInBody>>;
    };
    return customerService.deputySignIn(userId).then((response) => {
      this.send({
        type: 'csr.impersonation.changed',
        params: {
          session: response.data.payload.user,
          impersonation: response.data.payload.impersonation,
        },
      });
      return response;
    });
  }

  async completeLogin(response: AjsResponse<LoginBody>): Promise<void> {
    // The backend sets the `user` cookie during sign_in (Rails: add_cookies(json)).
    // The response payload shape can vary, so prefer the cookie as the source of
    // truth and fall back to the response only if the cookie is missing.
    const cookieUser = this.getUserFromCookie();
    const responseSession = response?.data?.session ?? null;
    const session = (cookieUser ?? responseSession) as UserSession | null;

    if (session == null || session.id == null) {
      // Throw an HTTP-response-shaped object so AngularJS controllers (e.g.
      // LoginController) that expect `IHttpResponse<WrappedACMEErrorResponse>`
      // in their catch handler can read `response.status` / `response.data.error`
      // without crashing.
      throw {
        status: 500,
        data: {
          success: false,
          code: 500,
          status: '500',
          error: 'Could not resolve user session ID after login',
          errorData: { type: 'SESSION_RESOLUTION_FAILED', message: 'Session ID missing' },
        },
      };
    }

    const userId = session.id;

    try {
      const csrfResponse = (await this.api.system.csrf()) as AjsResponse<CsrfBody>;
      this.setCSRF(csrfResponse.data.payload);

      await this.completePermissions(userId);

      this.send({ type: 'login.success', params: { session } });
    } catch (error) {
      const httpError = error as { status?: number };
      if (httpError?.status === 401 || httpError?.status === 440) {
        this.destroy();
      }
      throw error;
    }
  }

  private async completePermissions(userId: number): Promise<void> {
    const permResponse = (await this.api.user.permissions.list(userId)) as AjsResponse<PermissionsBody>;
    const permissions = permResponse.data.payload;

    // Bridged to the legacy TenantApiService until that sub-service is migrated.
    const tenant = this.api.tenant as { config: { list(): Promise<AjsResponse<TenantConfigBody>> } };
    const tenantResponse = await tenant.config.list();
    if (tenantResponse.data.payload.list.length > 0) {
      this.tenant.setModel(tenantResponse.data.payload.list[0]);
    }

    if (this.tenant.isRecurringPaymentEnabled() && permissions.indexOf('SUBSCRIPTION_VIEW') >= 0) {
      permissions.push('RECURRING_PAYMENT_ENABLED');
    }

    this.permissions.store(permissions);
  }

  destroy(): void {
    this.send({ type: 'logout' });
    removeCookie('session');
    removeCookie('user');
    removeCookie('utc');
    this.permissions.clearAll();
    // Do NOT clear the CSRF token here — the legacy auth.service.js kept the
    // token attached to `$http.defaults.headers` across logout/login cycles
    // and the backend accepts the same token for the next sign-in. Clearing
    // it here causes the next PUT /v1/api/users to be rejected with 422
    // "Can't verify CSRF token authenticity" until the page is refreshed
    // (which re-runs `AuthService.updateCSRF()` at bootstrap).
  }

  // ============= CSRF =============

  setCSRF(csrf: { token?: string; [key: string]: unknown } | null): void {
    this.CSRF = csrf;
    if (csrf?.token) {
      this.csrfInterceptor.setToken(csrf.token);
    }
  }

  getCSRF(key?: string): unknown {
    if (key && this.CSRF && Object.prototype.hasOwnProperty.call(this.CSRF, key)) {
      return this.CSRF[key];
    }
    return this.CSRF;
  }

  async updateCSRF(): Promise<void> {
    const csrfResponse = (await this.api.system.csrf()) as AjsResponse<CsrfBody>;
    this.setCSRF(csrfResponse.data.payload);
  }

  // ============= Cookies =============

  getCookie<T = unknown>(name: string): T | null {
    return getCookieValue<T>(name);
  }

  setCookie(name: string, value: unknown): void {
    setCookieValue(name, value);
  }

  killCookie(name: string): void {
    removeCookie(name);
  }

  // ============= Session info =============

  getUserFromCookie(): UserSession | null {
    const user = this.getCookie<UserSession>('user');
    // Guard against malformed cookies (e.g. an undecoded base64 string) — only
    // accept a real plain object before spreading it.
    if (user == null || typeof user !== 'object') {
      return null;
    }
    return { ...user, authenticationType: user.authenticationType ?? 'LEGACY' };
  }

  getCSRUserFromCookie(): UserSession | null {
    const csr = this.getCookie<UserSession>('csr');
    if (csr == null || typeof csr !== 'object') {
      return null;
    }
    return csr;
  }

  getUserFullName(): string {
    const csr = this.getCSRUserFromCookie();
    const csrLabel = this.mapUserSessionToLabel(csr);
    const userLabel = this.mapUserSessionToLabel(this.getUserFromCookie());

    if (csr == null) {
      return userLabel;
    }
    if (csrLabel === userLabel) {
      return `${csrLabel} (CSR)`;
    }
    return `${csrLabel} (as ${userLabel})`;
  }

  private mapUserSessionToLabel(
    props: { firstName?: string | null; lastName?: string | null; email?: string | null; userName?: string | null } | null,
  ): string {
    if (props == null) {
      return '';
    }
    if (props.firstName && props.lastName) {
      return `${props.firstName} ${props.lastName}`;
    }
    if (props.email) {
      return props.email;
    }
    if (props.userName) {
      return props.userName;
    }
    return '';
  }

  // ============= Roles =============

  hasRole(roles: string | string[]): boolean {
    this.getRoles();
    if (Array.isArray(roles)) {
      return roles.some((role) => this.roles.indexOf(role) >= 0);
    }
    return this.roles.indexOf(roles) >= 0;
  }

  getRoles(): string[] {
    const user = this.getUserFromCookie();
    const userRoles: string[] | string | null | undefined = user?.roles as
      | string[]
      | string
      | null
      | undefined;

    let split: string[] = [];
    if (typeof userRoles === 'string') {
      split = userRoles.indexOf(',') === -1 ? [userRoles] : userRoles.split(',');
    } else if (Array.isArray(userRoles)) {
      split = userRoles;
    }

    this.roles = split.length > 0 ? split.map((r) => r.replace(/\s+/g, '')) : [];
    if (user && this.roles.length === 0) {
      this.roles = ['user'];
    }
    return this.roles;
  }

  // ============= Permissions =============

  hasPermission(permissions: string | string[]): boolean {
    return this.permissions.check(permissions);
  }

  setPermissions(permissions: string[]): boolean {
    return this.permissions.store(permissions);
  }

  getPermissions(): Record<string, string> | false {
    return this.permissions.fetch();
  }

  hasPermissions(): boolean {
    return this.permissions.hasPermissions();
  }

  testChecksum(checksum: string): boolean {
    const current = this.permissions.fetchMD5?.();
    return checksum === current;
  }

  getFeatures(): unknown {
    return this.featureToggle.fetch();
  }

  // ============= Impersonation =============

  isCSR(): boolean {
    if (this.stateSubject.value.impersonation != null) {
      return true;
    }
    return this.permissions.check('CSR');
  }

  isCSRImpersonating(): boolean {
    return this.getImpersonation() != null;
  }

  getImpersonation(): Impersonation | null {
    if (this.stateSubject.value.impersonation != null) {
      return this.stateSubject.value.impersonation;
    }
    return this.getImpersonationFromCookies();
  }

  private getImpersonationFromCookies(): Impersonation | null {
    const csr = this.getCSRUserFromCookie();
    const user = this.getUserFromCookie();
    if (csr != null && user != null && csr.id !== user.id) {
      return { csr };
    }
    return null;
  }

  // ============= Auth type =============

  isSSO(): boolean {
    return this.getUserFromCookie()?.authenticationType === 'SSO';
  }

  isPasswordRotationEnabled(): boolean {
    return !!this.getUserFromCookie()?.passwordStatus;
  }

  // ============= Navigation =============

  getLoggedInPaths(): { state: string; url: string } | false {
    return {
      state: 'application.programs.list',
      url: '/app/programs/list',
    };
  }

  getHomeUrl(): string {
    const session = this.getCookie('session');
    if (session) {
      const path = this.getLoggedInPaths();
      if (path !== false) {
        return path.url;
      }
      return '';
    }
    return '/login';
  }

  getRedirectState(): string | false {
    const user = this.getUserFromCookie();
    if (user == null || !Object.prototype.hasOwnProperty.call(user, 'confirmed')) {
      return false;
    }
    if (!user.confirmed) {
      return 'onboard.default';
    }
    if (!user.onBoarded) {
      return 'onboard.default';
    }
    const path = this.getLoggedInPaths();
    return path !== false ? path.state : false;
  }

  // ============= Storage =============

  killStorage(): void {
    this.permissions.clearAll();
  }

  // ============= Initialization =============

  private initializeSession(): void {
    const session = this.getUserFromCookie();
    const impersonation = this.getImpersonationFromCookies();
    this.send({ type: 'session.initialized', params: { session, impersonation } });
  }
}
